import React, { useState } from 'react';
import {
  Flex,
  FormGroup,
  Grid,
  openNotificationWithIcon,
  PageContent,
  Regex,
  Switch,
  useFetch,
  useUserinfo,
} from '@mss/mss-component-library';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { ErrorMessage, Formik } from 'formik';
import { object, string } from 'yup';
import Form from 'formik-antd/lib/form';
import Input from 'formik-antd/lib/input';
import SubmitButton from 'jsx/common/SubmitButton';
import Button from 'jsx/common/Button';
import { Alert, notification, Spin, Tooltip } from 'antd';
import Fetch from 'jsx/common/util/_fetch';
import { Integration, IntegrationFormFields } from '..';
import isEmpty from 'lodash/isEmpty';
import { Info } from 'react-feather';
import TimeAgo from 'react-timeago';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { useConfigurationQuery } from '../../../../index';

function AddIntegration() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const { integrationId } = useParams<{ integrationId: string }>();
  const [syncAssets, setSyncAssets] = useState(false);
  const [isSyncing, setSyncing] = useState(false);
  const [t] = useTranslation(['configuration', 'common']);
  const { data: config } = useConfigurationQuery();
  const [hasPermission] = useUserinfo();
  const [connectionTestState, setConnectionTestState] = useState<{
    tested: boolean;
    success: boolean;
  }>({
    tested: false,
    success: false,
  });
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const urlRegex =
    /^(?=.{1,253}\.?$)(?:(?!-)[A-Za-z0-9-]{1,63}(?<!-)\.)+(?=.*[A-Za-z])(?:[A-Za-z0-9-]{2,63})(?<!-)$/;

  const jsonSchema = string()
    .test('json', t('Please enter a valid JSON'), value => {
      try {
        if (!value) throw new Error('Please enter a valid JSON');
        const parsed = JSON.parse(value);
        if (
          !parsed ||
          typeof parsed === 'boolean' ||
          typeof parsed === 'number'
        ) {
          return false;
        }
      } catch {
        return false;
      }
      return true;
    })
    .required(t('Required'));

  const IntegrationSchema = (t: TFunction) =>
    object().shape({
      name: string()
        .required('Required')
        .trim(t('Cannot start or end with whitespace'))
        .strict(true),

      user: string().required('Required'),
      password: string().required('Required'),
      host: string()
        .required('Required')
        .test(
          'host-validation',
          t('Please enter a valid host or IP'),
          value => {
            if (!value) return false;
            const isValidUrl = urlRegex.test(value);
            const isValidIp = Regex.ip.test(value);
            return isValidUrl || isValidIp;
          }
        )
        .required('Required'),
      domain: syncAssets ? string().required('Required') : string(),
      json_schema: jsonSchema,
      json_schema_case: jsonSchema,
    });
  const JsonValidation = (value: string): string | undefined => {
    try {
      const parsed = JSON.parse(value);
      if (
        !parsed ||
        typeof parsed === 'boolean' ||
        typeof parsed === 'number'
      ) {
        return 'Please enter a valid JSON';
      }
    } catch {
      return 'Please enter a valid JSON';
    }
    return undefined;
  };

  const parseIntegrationData = (integration: Integration): Integration => {
    const baseIntegration = {
      ...integration,
      json_schema: JSON.stringify(integration.json_schema, null, 2),
      json_schema_case: JSON.stringify(integration.json_schema_case, null, 2),
      field_mapping: JSON.stringify(integration.field_mapping, null, 2),
      field_mapping_case: JSON.stringify(
        integration.field_mapping_case,
        null,
        2
      ),
    };

    if (Array.isArray(integration.last_sync_status)) {
      return {
        ...baseIntegration,
        assets: integration.last_sync_status,
      };
    }

    return {
      ...baseIntegration,
      assets: [],
      error_sync: integration.last_sync_status?.error,
    };
  };

  const [{ data: integration, loading }, refetchIntegrations] = useFetch({
    url: `/api/servicenowconfig/${integrationId}/`,
    defaultValue: {},
    dataParser: parseIntegrationData,
    initialFetch: !!integrationId,
  });

  const goToIntegrationsList = () => {
    history.push(`${url.replace(`/add`, '')}`);
  };

  const editIntegration = !!(integrationId && integration && !loading);
  const disabledEdit =
    editIntegration && !hasPermission(['change_servicenowconfig']);
  const errorMsg = async (resp: Response, action: 'edit' | 'add') => {
    const respData = await resp.json?.();
    if (respData) {
      const error =
        respData.non_field_errors?.length > 0
          ? respData.non_field_errors[0]
          : respData[0];
      openNotificationWithIcon(notification.error, t(error), '');
    } else {
      const msg = {
        edit: 'Integration update failed',
        add: 'Failed to create integration.',
      };
      openNotificationWithIcon(notification.error, t(msg[action]), '');
    }
  };

  const onCreate = async (values: IntegrationFormFields, setFieldValue?: any) => {
    setIsLoadingSave(true);
    const resp = await Fetch.post(`/api/servicenowconfig/`, {
      body: JSON.stringify({
        ...values,
        json_schema: JSON.parse(values.json_schema),
        json_schema_case: JSON.parse(values.json_schema_case),
        field_mapping: JSON.parse(values.field_mapping),
        field_mapping_case: JSON.parse(values.field_mapping_case),
      }),
    });

    if (resp.status === 201) {
      openNotificationWithIcon(
        notification.success,
        t('Integration created successfully.'),
        ''
      );
      if (setFieldValue) {
        setFieldValue('password', '', false);
      }
      setConnectionTestState({
        tested: false,
        success: false,
      });
      goToIntegrationsList();
    } else {
      await errorMsg(resp, 'add');
    }
    setIsLoadingSave(false);
  };

  const onEdit = async (values: IntegrationFormFields, setFieldValue?: any) => {
    setIsLoadingSave(true);
    const resp = await Fetch.put(`/api/servicenowconfig/${integrationId}/`, {
      body: JSON.stringify({
        ...values,
        json_schema: JSON.parse(values.json_schema),
        json_schema_case: JSON.parse(values.json_schema_case),
        field_mapping: JSON.parse(values.field_mapping),
        field_mapping_case: JSON.parse(values.field_mapping_case),
      }),
    });

    if (resp.status === 200) {
      openNotificationWithIcon(
        notification.success,
        t('Integration updated successfully'),
        ''
      );
      if (setFieldValue) {
        setFieldValue('password', '', false);
      }
      setConnectionTestState({
        tested: false,
        success: false,
      });
      goToIntegrationsList();
    } else {
      await errorMsg(resp, 'add');
    }
    goToIntegrationsList();
    setIsLoadingSave(false);
  };

  const onTestConnection = async (values: IntegrationFormFields) => {
    setIsLoadingSave(true);
    setConnectionTestState({
      tested: true,
      success: false,
    });
    const resp = await Fetch.post(`/api/servicenowconfig/test_connection/`, {
      body: JSON.stringify({
        user: values.user,
        password: values.password,
        host: values.host,
      }),
    });

    if (resp.status === 200) {
      openNotificationWithIcon(
        notification.success,
        t('Connection successful'),
        ''
      );
      setConnectionTestState({
        tested: true,
        success: true,
      });
    }
    else {
      openNotificationWithIcon(notification.error, t('Connection failed'), '');
      setConnectionTestState({
        tested: true,
        success: false,
      });
    }
    setIsLoadingSave(false);
  };

  const syncCMDBAssets = async () => {
    setSyncing(true);
    const resp = await Fetch.post(`/api/servicenowconfig/sync_assets/`, {
      body: JSON.stringify({
        servicenow_id: integrationId,
      }),
    });

    if (resp.status !== 200) {
      setSyncing(false);
      openNotificationWithIcon(
        notification.error,
        t('Sync command failed'),
        ''
      );
    }

    if (resp.status === 200) {
      openNotificationWithIcon(
        notification.success,
        t('Sync command triggered successfully'),
        ''
      );
      setTimeout(function () {
        setSyncing(false);
        refetchIntegrations();
      }, 5000);
    }
  };
  const getSaveButtonText = () => {
    if (isLoadingSave) {
      return t('Save');
    }
    if (connectionTestState.tested && !connectionTestState.success) {
      return t('Save despite failed test');
    }
    return t('Save');
  };

  return loading ? (
    <Flex v="center" h="center">
      <Spin />
    </Flex>
  ) : (
    <PageContent
      title={editIntegration ? t('Edit integration') : t('New integration')}
    >
      {editIntegration && (
        <Alert
          type="info"
          className="mb-12"
          message={t(
            'Username, password and host of SNOW instance are required to make the changes.'
          )}
        />
      )}
      <Formik
        initialValues={{
          name: integration?.name || '',
          type: integration?.type || '',
          user: integration?.user || '',
          password: integration?.password || '',
          host: integration?.host || '',
          json_schema: integration?.json_schema || '{}',
          json_schema_case: integration?.json_schema_case || '{}',
          field_mapping: integration?.field_mapping || '{}',
          field_mapping_case: integration?.field_mapping_case || '{}',
          sync_assets: integration?.sync_assets || false,
          domain: integration?.domain || '',
          last_sync_status: integration?.last_sync_status || [],
          last_sync_time: integration?.last_sync_time,
          assets: integration?.assets,
          error_sync: integration?.error_sync,
          site: integration?.site || '',
        }}
        validationSchema={IntegrationSchema(t)}
        onSubmit={(values: IntegrationFormFields, { setFieldValue }) => {
          if (editIntegration) {
            onEdit(values, setFieldValue);
          } else {
            onCreate(values, setFieldValue);
          }
        }}
      >
        {({ values, errors, handleBlur, handleChange, setFieldValue }) => {
          // TODO: oh no.. setting state in render :/
          setSyncAssets(values.sync_assets);
          const isSaveDisabled =
            !connectionTestState.tested ||
            isLoadingSave ||
            disabledEdit ||
            !isEmpty(errors);
          return (
            <Form>
              <Grid.Row>
                <Grid.Col width={12}>
                  <FormGroup
                    label={t('Name')}
                    required={true}
                    error={
                      <ErrorMessage
                        name="name"
                        render={(errorMsg: string) => t(errorMsg)}
                      />
                    }
                    input={
                      <Input
                        name={'name'}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.name}
                        disabled={disabledEdit}
                      />
                    }
                  />
                </Grid.Col>
              </Grid.Row>
              <Grid.Row>
                <Grid.Col width={6}>
                  <FormGroup
                    label={t('Username')}
                    required={true}
                    error={
                      <ErrorMessage
                        name="user"
                        render={(errorMsg: string) => t(errorMsg)}
                      />
                    }
                    input={
                      <Input
                        name={'user'}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.user}
                        disabled={disabledEdit}
                      />
                    }
                  />
                </Grid.Col>
                <Grid.Col width={6}>
                  <FormGroup
                    label={t('Password')}
                    required={true}
                    error={
                      <ErrorMessage
                        name="password"
                        render={(errorMsg: string) => t(errorMsg)}
                      />
                    }
                    input={
                      <Input.Password
                        name={'password'}
                        iconRender={visible =>
                          visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.password}
                        disabled={disabledEdit}
                      />
                    }
                  />
                </Grid.Col>
              </Grid.Row>
              <Grid.Row>
                <Grid.Col width={6}>
                  <FormGroup
                    label={t('Host')}
                    required={true}
                    error={
                      <ErrorMessage
                        name="host"
                        render={(errorMsg: string) => t(errorMsg)}
                      />
                    }
                    input={
                      <Input
                        name={'host'}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.host}
                        disabled={disabledEdit}
                      />
                    }
                  />
                </Grid.Col>
                <Grid.Col width={6}>
                  <FormGroup
                    label={t('Domain')}
                    required={values.sync_assets}
                    error={
                      <ErrorMessage
                        name="domain"
                        render={(errorMsg: string) => t(errorMsg)}
                      />
                    }
                    input={
                      <Input
                        name={'domain'}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.domain}
                        disabled={disabledEdit}
                      />
                    }
                  />
                </Grid.Col>
              </Grid.Row>
              <Grid.Row>
                <Grid.Col width={12}>
                  <FormGroup
                    label={t('Sync Assets')}
                    error={
                      <ErrorMessage
                        name="sync_assets"
                        render={(errorMsg: string) => t(errorMsg)}
                      />
                    }
                    input={
                      <Switch
                        name={'sync_assets'}
                        onChange={value => {
                          setSyncAssets(value);
                          setFieldValue('sync_assets', value);
                        }}
                        onBlur={handleBlur}
                        value={values.sync_assets}
                        disabled={disabledEdit}
                        checked={values.sync_assets}
                      />
                    }
                  />
                </Grid.Col>
              </Grid.Row>
              <Grid.Row>
                <Grid.Col width={12}>
                  <FormGroup
                    required
                    label={
                      <Flex h="start" v="start" inline>
                        {t('Incident JSON Schema')}

                        <Tooltip title={t('Valid JSON schema')}>
                          <Info
                            className="ml-1 text-gray-500"
                            size={12}
                            fill={'#417e80'}
                            color={'#ffffff'}
                          />
                        </Tooltip>
                      </Flex>
                    }
                    error={
                      <ErrorMessage
                        name="json_schema"
                        render={(errorMsg: string) => t(errorMsg)}
                      />
                    }
                    input={
                      <Input.TextArea
                        name={'json_schema'}
                        rows={7}
                        required
                        onChange={handleChange}
                        onBlur={handleBlur}
                        validate={value => JsonValidation(value)}
                        value={values.json_schema}
                        disabled={disabledEdit}
                      />
                    }
                  />
                </Grid.Col>
                <Grid.Col width={12}>
                  <FormGroup
                    required
                    label={
                      <Flex h="start" v="start" inline>
                        {t('Case JSON Schema')}

                        <Tooltip title={t('Valid JSON schema')}>
                          <Info
                            className="ml-1 text-gray-500"
                            size={12}
                            fill={'#417e80'}
                            color={'#ffffff'}
                          />
                        </Tooltip>
                      </Flex>
                    }
                    error={
                      <ErrorMessage
                        name="json_schema_case"
                        render={(errorMsg: string) => t(errorMsg)}
                      />
                    }
                    input={
                      <Input.TextArea
                        name={'json_schema_case'}
                        rows={7}
                        required
                        onChange={handleChange}
                        onBlur={handleBlur}
                        validate={value => JsonValidation(value)}
                        value={values.json_schema_case}
                        disabled={disabledEdit}
                      />
                    }
                  />
                </Grid.Col>
              </Grid.Row>
              <Grid.Row>
                <Grid.Col width={12}>
                  <FormGroup
                    required
                    label={
                      <Flex h="start" v="start" inline>
                        {t('Incident Mapping')}

                        <Tooltip
                          title={t(
                            'Valid JSON that describes the values from ServiceNow (can be different for each instance)'
                          )}
                        >
                          <Info
                            className="ml-1 text-gray-500"
                            size={12}
                            fill={'#417e80'}
                            color={'#ffffff'}
                          />
                        </Tooltip>
                      </Flex>
                    }
                    error={
                      <ErrorMessage
                        name="field_mapping"
                        render={(errorMsg: string) => t(errorMsg)}
                      />
                    }
                    input={
                      <Input.TextArea
                        name={'field_mapping'}
                        rows={7}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        validate={value => JsonValidation(value)}
                        value={values.field_mapping}
                        disabled={disabledEdit}
                      />
                    }
                  />
                </Grid.Col>
                <Grid.Col width={12}>
                  <FormGroup
                    required
                    label={
                      <Flex h="start" v="start" inline>
                        {t('Case Mapping')}

                        <Tooltip
                          title={t(
                            'Valid JSON that describes the values from ServiceNow (can be different for each instance)'
                          )}
                        >
                          <Info
                            className="ml-1 text-gray-500"
                            size={12}
                            fill={'#417e80'}
                            color={'#ffffff'}
                          />
                        </Tooltip>
                      </Flex>
                    }
                    error={
                      <ErrorMessage
                        name="field_mapping_case"
                        render={(errorMsg: string) => t(errorMsg)}
                      />
                    }
                    input={
                      <Input.TextArea
                        name={'field_mapping_case'}
                        rows={7}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        validate={value => JsonValidation(value)}
                        value={values.field_mapping_case}
                        disabled={disabledEdit}
                      />
                    }
                  />
                </Grid.Col>
              </Grid.Row>
              <Grid.Row>
                <Grid.Col width={12}>
                  <Flex h="end" v="center">
                    {values.sync_assets &&
                      integration?.last_sync_time &&
                      integration?.assets &&
                      !integration?.error_sync &&
                      !config?.unidirectional && (
                        <div className="mr-1">
                          <Tooltip
                            open={!integration?.last_sync_time ? false : undefined}
                            title={
                              <span style={{ fontSize: 12, lineHeight: 1 }}>
                                <span style={{ fontSize: 12, lineHeight: 1.4 }}>
                                  <span>Synchronized assets: {integration.assets.length}</span>
                                </span>
                              </span>
                            }
                          >
                            <Info size={15} className="text-success" />
                          </Tooltip>
                        </div>
                      )}
                    {values.sync_assets &&
                      integration?.error_sync &&
                      !config?.unidirectional && (
                        <div className="mr-1">
                          <Tooltip
                            title={
                              <span style={{ fontSize: 12, lineHeight: 1 }}>
                                {t('Failed')}: {integration.error_sync}
                              </span>
                            }
                          >
                            <Info size={15} className="text-danger" />
                          </Tooltip>
                        </div>
                      )}
                    {values.sync_assets && !config?.unidirectional && (
                      <small className="mr-4 text-gray-500">
                        {`${t('Last synchronization')}: `}{' '}
                        <strong>
                          {integration?.last_sync_time ? (
                            <TimeAgo date={integration.last_sync_time} />
                          ) : (
                            t('unknown')
                          )}
                        </strong>
                      </small>
                    )}

                    {editIntegration && (
                      <Button
                        disabled={
                          !values.sync_assets || errors.domain || disabledEdit
                        }
                        loading={isSyncing}
                        variant="primary"
                        onClick={syncCMDBAssets}
                        className="mr-2"
                        data-ta="syncIntegrationAssets"
                      >
                        {t('Sync Now')}
                      </Button>
                    )}
                    {!config?.unidirectional && (
                      <Button
                        disabled={
                          errors.host ||
                          errors.user ||
                          errors.password ||
                          disabledEdit
                        }
                        variant="primary"
                        onClick={() => onTestConnection(values)}
                        className="mr-2"
                        data-ta="testIntegrationConnection"
                      >
                        {t('Test connection')}
                      </Button>
                    )}
                    <SubmitButton disabled={isSaveDisabled}
                      loading={isLoadingSave}
                      ghost={connectionTestState.tested && !connectionTestState.success}
                      variant={
                        connectionTestState.tested && !connectionTestState.success
                          ? 'default'
                          : 'primary'
                      }
                      colorOverrides={
                        connectionTestState.tested && !connectionTestState.success
                          ? {
                            text: '#fa4654',
                            border: '#fa4654',
                          }
                          : undefined
                      }>
                      {getSaveButtonText()}
                    </SubmitButton>
                  </Flex>
                </Grid.Col>
              </Grid.Row>
            </Form>
          );
        }}
      </Formik>
    </PageContent>
  );
}

export default AddIntegration;
